/**
 * Turn a platform-session failure into an accurate, user-facing description.
 *
 * The session provider surfaces whatever `getCurrentSession()` throws, and that
 * is one of three very different things: a {@link PlatformApiError} carrying the
 * API's status and message, a `TypeError` from `fetch` when the API cannot be
 * reached at all, or an unexpected error. Rendering one fixed sentence for all
 * of them — as the shell once did, always "no active tenant membership" — tells
 * a user whose network dropped that their account lacks access, which is both
 * wrong and unactionable, and tells an unprovisioned but validly signed-in user
 * the same thing. Each branch here names what actually failed.
 *
 * The API already writes good, boundary-safe wording for the domain cases (a
 * 403 reads "You do not have access to this organisation.", deliberately
 * uniform so it cannot be used to enumerate tenants), so those are surfaced
 * as-is. Only the network case substitutes its own text, because the raw
 * `fetch` message ("Failed to fetch", "Load failed") is not something to show a
 * user.
 *
 * @module platform/session-error
 */

import { PlatformApiError } from "./api-error";

/** An accurate title and detail for a failed session resolution. */
export interface SessionErrorDescription {
  /** Short headline naming the actual failure. */
  title: string;
  /** A sentence the user can act on. */
  detail: string;
}

const UNREACHABLE: SessionErrorDescription = {
  title: "Couldn't reach the service",
  detail: "The app couldn't contact the platform API. Check your connection and try again.",
};

const UNEXPECTED: SessionErrorDescription = {
  title: "Your session couldn't be loaded",
  detail: "Something went wrong resolving your session. Try again.",
};

/**
 * Whether an error is a `fetch` transport failure rather than an API response.
 *
 * A failed `fetch` rejects with a `TypeError` — the request never reached the
 * server, so there is no status to read. The message text varies by engine
 * ("Failed to fetch" in Chromium, "Load failed" in WebKit, "NetworkError" in
 * Firefox), so both the type and a text match are checked.
 *
 * @param error - The caught error.
 * @returns `true` when the failure is a transport error.
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return true;
  }
  if (error instanceof Error) {
    return /failed to fetch|networkerror|illegal invocation|load failed/i.test(error.message);
  }
  return false;
}

/**
 * Describe a session-resolution failure accurately.
 *
 * @param error - The value the session provider caught.
 * @returns A title and detail that match the actual failure.
 */
export function describeSessionError(error: unknown): SessionErrorDescription {
  if (error instanceof PlatformApiError) {
    const detail = error.message || UNEXPECTED.detail;
    if (error.status === 401) {
      return { title: "This account isn't recognised here", detail };
    }
    if (error.status === 403) {
      return { title: "No access to this organisation", detail };
    }
    if (error.status >= 500) {
      return { title: "The service ran into a problem", detail };
    }
    return { title: UNEXPECTED.title, detail };
  }

  if (isNetworkError(error)) {
    return UNREACHABLE;
  }

  return UNEXPECTED;
}
